/**
 * Batch Selector Component
 *
 * Allows manual selection of specific inventory batches with quantities.
 * Used when overriding FIFO for ISO 13485 traceability requirements.
 *
 * @module components/inventory/BatchSelector
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Table, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { getInventoryBatches } from '../../services/inventoryService';
import { formatCurrency, formatDate, isOverdue } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';

const BatchSelector = ({ inventoryId, unit, onSelectionChange }) => {
  const { isAdmin } = useAuth();
  const [selections, setSelections] = useState({});

  // Fetch available batches
  const { data: batchesResult, isLoading, error } = useQuery({
    queryKey: ['inventory-batches', inventoryId],
    queryFn: () => getInventoryBatches(inventoryId, { activeOnly: true }),
    enabled: !!inventoryId,
  });

  const batches = useMemo(() => batchesResult?.data || [], [batchesResult]);

  // Calculate totals whenever selections change
  const { totalQuantity, totalCost, selectedBatches } = useMemo(() => {
    let qty = 0;
    let cost = 0;
    const selected = [];

    for (const batch of batches) {
      const amount = selections[batch.id] || 0;
      if (amount > 0) {
        qty += amount;
        cost += amount * batch.unit_cost;
        selected.push({
          batchId: batch.id,
          lotNumber: batch.lot_number,
          quantity: amount,
          unitCost: batch.unit_cost,
          totalCost: amount * batch.unit_cost,
        });
      }
    }

    return {
      totalQuantity: qty,
      totalCost: Math.round(cost * 100) / 100,
      selectedBatches: selected,
    };
  }, [selections, batches]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange({
      selections: Object.entries(selections)
        .filter(([, qty]) => qty > 0)
        .map(([batchId, quantity]) => ({ batchId, quantity })),
      totalQuantity,
      totalCost,
      selectedBatches,
    });
  }, [selections, totalQuantity, totalCost, selectedBatches, onSelectionChange]);

  const handleQuantityChange = (batchId, value) => {
    const numValue = parseFloat(value) || 0;
    const batch = batches.find(b => b.id === batchId);
    const maxQty = batch ? batch.quantity_available : 0;
    const clamped = Math.max(0, Math.min(numValue, maxQty));

    setSelections(prev => ({
      ...prev,
      [batchId]: clamped,
    }));
  };

  if (isLoading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" className="me-2" />
        <small>Cargando lotes...</small>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        <small>Error al cargar lotes: {error.message}</small>
      </Alert>
    );
  }

  if (batches.length === 0) {
    return (
      <Alert variant="warning" className="mb-0">
        <small>
          <i className="bi bi-exclamation-triangle me-1"></i>
          No hay lotes disponibles para este item
        </small>
      </Alert>
    );
  }

  return (
    <div>
      <div className="table-responsive" style={{ maxHeight: '280px', overflowY: 'auto' }}>
        <Table size="sm" hover className="mb-0">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ minWidth: '90px' }}>Lote</th>
              <th>Proveedor</th>
              <th className="text-end" style={{ minWidth: '80px' }}>Disponible</th>
              {/* admin-only: purchase cost columns */}
              {isAdmin && <th className="text-end" style={{ minWidth: '90px' }}>Costo/u</th>}
              <th style={{ minWidth: '70px' }}>Expira</th>
              <th style={{ minWidth: '100px' }}>Cantidad</th>
              {isAdmin && <th className="text-end" style={{ minWidth: '90px' }}>Subtotal</th>}
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => {
              const qty = selections[batch.id] || 0;
              const isSelected = qty > 0;
              const subtotal = qty * batch.unit_cost;

              return (
                <tr
                  key={batch.id}
                  className={isSelected ? 'table-primary' : ''}
                >
                  <td>
                    <small className="fw-medium">
                      {batch.lot_number || '-'}
                    </small>
                    {batch.location && (
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                        <i className="bi bi-geo-alt me-1"></i>{batch.location}
                      </small>
                    )}
                  </td>
                  <td>
                    <small>{batch.purchase?.supplier?.name || '-'}</small>
                  </td>
                  <td className="text-end">
                    <small>{batch.quantity_available} {unit}</small>
                  </td>
                  {/* admin-only: purchase cost per unit */}
                  {isAdmin && (
                    <td className="text-end">
                      {/* #617: el lote SI tiene moneda propia, se usa la suya */}
                      <small>{formatCurrency(batch.unit_cost, batch.currency)}</small>
                    </td>
                  )}
                  <td>
                    <small>
                      {batch.expiration_date ? (
                        <span className={
                          isOverdue(batch.expiration_date)
                            ? 'text-danger'
                            : ''
                        }>
                          {formatDate(batch.expiration_date)}
                        </span>
                      ) : '-'}
                    </small>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      min="0"
                      max={batch.quantity_available}
                      step="0.01"
                      value={qty || ''}
                      onChange={(e) => handleQuantityChange(batch.id, e.target.value)}
                      placeholder="0"
                      style={{ width: '80px' }}
                    />
                  </td>
                  {/* admin-only: line subtotal cost */}
                  {isAdmin && (
                    <td className="text-end">
                      <small className={isSelected ? 'fw-bold' : 'text-muted'}>
                        {isSelected ? formatCurrency(subtotal) : '-'}
                      </small>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Summary footer */}
      <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top">
        <div>
          <Badge bg={totalQuantity > 0 ? 'primary' : 'secondary'} className="me-2">
            {totalQuantity} {unit}
          </Badge>
          <small className="text-muted">
            de {batches.length} lote{batches.length !== 1 ? 's' : ''} disponible{batches.length !== 1 ? 's' : ''}
          </small>
        </div>
        {/* admin-only: total cost footer */}
        {isAdmin && (
          <div className="text-end">
            <small className="text-muted d-block">Costo total</small>
            <strong className="text-primary">
              {formatCurrency(totalCost)}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
};

BatchSelector.propTypes = {
  inventoryId: PropTypes.string.isRequired,
  unit: PropTypes.string,
  onSelectionChange: PropTypes.func.isRequired,
};

BatchSelector.defaultProps = {
  unit: 'unidad',
};

export default BatchSelector;
